import { useEffect, useMemo, useRef, useState } from 'react';
import {
  resolveClipTextStickerStyle,
  resolveSubtitleAppearance,
  type ClipEffectItem,
  type ClipImageStickerEffect,
  type ClipStickerEffect,
  type ClipTextStickerEffect,
  type SubtitleStyle,
} from '@packages/global';

interface ClipEffectsRendererProps {
  effectItems: ClipEffectItem[];
  selectedEffectId?: string | null;
  editable?: boolean;
  baseSubtitleStyle?: SubtitleStyle;
  onSelect?: (effectId: string) => void;
  onChangeSticker?: (effectId: string, sticker: ClipStickerEffect) => void;
}

interface DragState {
  type: 'move' | 'resize';
  effectId: string;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  originWidthRatio: number;
  originHeightRatio: number;
  aspectRatio: number;
  stickerType: ClipStickerEffect['type'];
}

export default function ClipEffectsRenderer({
  effectItems,
  selectedEffectId,
  editable = false,
  baseSubtitleStyle,
  onSelect,
  onChangeSticker,
}: ClipEffectsRendererProps) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [imageAspectMap, setImageAspectMap] = useState<Record<string, number>>({});
  const [fontFamilyMap, setFontFamilyMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const textStickers = effectItems
      .filter((item): item is ClipEffectItem & { sticker: ClipTextStickerEffect } => item.sticker?.type === 'text')
      .map((item) => ({ effectId: item.id, fontUrl: item.sticker.style?.fontUrl }));

    let cancelled = false;

    Promise.all(textStickers.map(async ({ effectId, fontUrl }) => {
      if (!fontUrl) {
        return [effectId, ''] as const;
      }

      const family = `VideoAgentEffectSticker-${effectId}`;
      try {
        const fontFace = new FontFace(family, `url(${fontUrl})`);
        await fontFace.load();
        document.fonts.add(fontFace);
        return [effectId, family] as const;
      } catch {
        return [effectId, ''] as const;
      }
    })).then((entries) => {
      if (cancelled) return;
      setFontFamilyMap(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [effectItems]);

  useEffect(() => {
    if (!dragState) return;

    const handleMove = (event: PointerEvent) => {
      const overlay = overlayRef.current;
      const currentItem = effectItems.find((item) => item.id === dragState.effectId);
      const sticker = currentItem?.sticker;
      if (!overlay || !sticker || !onChangeSticker) return;

      const overlayWidth = overlay.clientWidth || 1;
      const overlayHeight = overlay.clientHeight || 1;
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      if (dragState.type === 'move') {
        const nextX = Math.min(
          1 - dragState.originWidthRatio,
          Math.max(0, dragState.originX + deltaX / overlayWidth),
        );
        const nextY = Math.min(
          1 - dragState.originHeightRatio,
          Math.max(0, dragState.originY + deltaY / overlayHeight),
        );

        onChangeSticker(dragState.effectId, {
          ...sticker,
          x: Number(nextX.toFixed(4)),
          y: Number(nextY.toFixed(4)),
        });
        return;
      }

      if (dragState.stickerType === 'text' && sticker.type === 'text') {
        const nextWidthRatio = Math.min(1, Math.max(0.08, dragState.originWidthRatio + deltaX / overlayWidth));
        const maxWidthRatio = Math.max(0.08, 1 - dragState.originX);
        const clampedWidthRatio = Math.min(nextWidthRatio, maxWidthRatio);

        onChangeSticker(dragState.effectId, {
          ...sticker,
          widthRatio: Number(clampedWidthRatio.toFixed(4)),
        });
        return;
      }

      const nextWidthRatio = Math.min(1, Math.max(0.05, dragState.originWidthRatio + deltaX / overlayWidth));
      const nextHeightRatio = Math.min(
        1,
        Math.max(0.05, (nextWidthRatio * overlayWidth) / (dragState.aspectRatio * overlayHeight)),
      );
      const maxWidthRatio = Math.max(0.05, 1 - dragState.originX);
      const maxHeightRatio = Math.max(0.05, 1 - dragState.originY);
      const clampedWidthRatio = Math.min(nextWidthRatio, maxWidthRatio);
      const clampedHeightRatio = Math.min(nextHeightRatio, maxHeightRatio);

      onChangeSticker(dragState.effectId, {
        ...sticker,
        widthRatio: Number(clampedWidthRatio.toFixed(4)),
        heightRatio: Number(clampedHeightRatio.toFixed(4)),
      } as ClipImageStickerEffect);
    };

    const handleUp = () => setDragState(null);

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [dragState, effectItems, onChangeSticker]);

  const renderedItems = useMemo(
    () => effectItems.filter((item) => item.sticker),
    [effectItems],
  );

  return (
    <div ref={overlayRef} className="absolute inset-0">
      {renderedItems.map((item) => {
        const sticker = item.sticker!;
        const isSelected = selectedEffectId === item.id;
        const aspectRatio = sticker.type === 'image' ? (imageAspectMap[item.id] || 1) : 1;
        const heightRatio = sticker.type === 'image'
          ? (sticker.heightRatio || Math.max(0.05, sticker.widthRatio / aspectRatio))
          : Math.max(0.08, sticker.fontSizeRatio ?? 0.09);
        const overlayHeight = overlayRef.current?.clientHeight || 720;

        const commonStyle = {
          left: `${sticker.x * 100}%`,
          top: `${sticker.y * 100}%`,
          width: `${sticker.widthRatio * 100}%`,
          ...(sticker.type === 'image' ? { height: `${heightRatio * 100}%` } : {}),
          transform: sticker.rotation ? `rotate(${sticker.rotation}deg)` : undefined,
          transformOrigin: 'center center' as const,
        };

        return (
          <div
            key={item.id}
            className={`absolute ${editable ? 'cursor-move' : ''}`}
            style={commonStyle}
            onPointerDown={(event) => {
              event.stopPropagation();
              onSelect?.(item.id);
              if (!editable || !onChangeSticker) return;

              const overlay = overlayRef.current;
              const box = event.currentTarget.getBoundingClientRect();
              const overlayBox = overlay?.getBoundingClientRect();
              const originHeightRatio = overlayBox?.height
                ? Math.min(1, Math.max(0.05, box.height / overlayBox.height))
                : heightRatio;

              setDragState({
                type: 'move',
                effectId: item.id,
                startX: event.clientX,
                startY: event.clientY,
                originX: sticker.x,
                originY: sticker.y,
                originWidthRatio: sticker.widthRatio,
                originHeightRatio,
                aspectRatio,
                stickerType: sticker.type,
              });
            }}
          >
            {sticker.type === 'image' ? (
              <img
                src={sticker.imageUrl}
                alt=""
                draggable={false}
                className={`w-full h-full object-contain select-none ${isSelected ? 'drop-shadow-[0_0_10px_rgba(59,130,246,0.45)]' : ''}`}
                style={{ height: `${heightRatio * 100}%` }}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  const naturalAspect = image.naturalWidth && image.naturalHeight
                    ? image.naturalWidth / image.naturalHeight
                    : 1;
                  setImageAspectMap((prev) => ({ ...prev, [item.id]: naturalAspect }));
                  if (!sticker.heightRatio && onChangeSticker) {
                    const overlay = overlayRef.current;
                    const overlayWidth = overlay?.clientWidth || 1;
                    const overlayHeight = overlay?.clientHeight || 1;
                    const inferredHeightRatio = Math.min(
                      1,
                      Math.max(0.05, (sticker.widthRatio * overlayWidth) / (naturalAspect * overlayHeight)),
                    );
                    onChangeSticker(item.id, {
                      ...sticker,
                      heightRatio: Number(inferredHeightRatio.toFixed(4)),
                    });
                  }
                }}
              />
            ) : (
              (() => {
                const resolvedStyle = resolveClipTextStickerStyle(sticker, baseSubtitleStyle);
                const fontSizePx = Math.max(16, Math.round((sticker.fontSizeRatio ?? 0.09) * overlayHeight));
                const appearance = resolveSubtitleAppearance(resolvedStyle, fontSizePx);
                return (
                  <div
                    className={`select-none whitespace-pre-wrap break-words leading-[1.2] ${isSelected ? 'drop-shadow-[0_0_10px_rgba(59,130,246,0.35)]' : ''}`}
                    style={{
                      minHeight: `${heightRatio * 100}%`,
                      color: appearance.textColor,
                      textShadow: appearance.cssTextShadow,
                      fontFamily: fontFamilyMap[item.id] || resolvedStyle.fontName || undefined,
                      fontWeight: sticker.style?.bold ? 700 : 600,
                      fontStyle: sticker.style?.italic ? 'italic' : 'normal',
                      fontSize: `${fontSizePx}px`,
                      textAlign: 'center',
                    }}
                  >
                    {sticker.text}
                  </div>
                );
              })()
            )}

            {editable && isSelected && (
              <>
                <div className="absolute inset-0 rounded border-2 border-cyan-400 pointer-events-none" />
                <button
                  type="button"
                  className="absolute -right-2 -bottom-2 w-4 h-4 rounded-full border border-white bg-cyan-400 shadow"
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    if (!onChangeSticker) return;
                    setDragState({
                      type: 'resize',
                      effectId: item.id,
                      startX: event.clientX,
                      startY: event.clientY,
                      originX: sticker.x,
                      originY: sticker.y,
                      originWidthRatio: sticker.widthRatio,
                      originHeightRatio: heightRatio,
                      aspectRatio,
                      stickerType: sticker.type,
                    });
                  }}
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
